import React, { useCallback } from 'react';

import { useEditorDoc, useEditorDispatch, useEditorMeta } from '../../EditorProvider';
import { cellIndex, type SudokuDoc } from './doc';
import type { SudokuAction } from './reducer';
import { Cell } from './Cell';

// A grade (9×9 ou 15×15). Clicar numa casa alterna dada ↔ vazia — é a única edição que o
// tabuleiro aceita (D1: o admin gera e ajusta, não desenha).
//
// Este é o ÚNICO consumidor do DocContext (regra de perf do §8). O handler passado
// à `Cell` precisa de referência ESTÁVEL entre renders, senão o `React.memo` das
// casas nunca bate e um clique re-renderiza a grade inteira. Aqui ele é estável de graça: a
// única dependência é o `do` do dispatch, que o `EditorProvider` memoiza para
// sempre — nada de `doc` nem de `ui` entra no `useCallback` (é o mesmo cuidado que
// `wordsearch/Canvas.tsx` resolve com `stateRef`; o sudoku não precisa nem disso,
// porque a ação carrega só o índice da casa).

export const Canvas: React.FC = () => {
  const doc = useEditorDoc<SudokuDoc>();
  const { do: dispatchAction } = useEditorDispatch();
  const { editavel } = useEditorMeta();

  const onToggle = useCallback(
    (index: number) => {
      const action: SudokuAction = { type: 'TOGGLE_GIVEN', index };
      dispatchAction(action);
    },
    [dispatchAction],
  );

  const { size, boxW, boxH } = doc.shape;
  const px = size > 9 ? 28 : 40;

  return (
    <div style={{ display: 'inline-block', padding: 8 }} data-testid="sudoku-canvas">
      {Array.from({ length: size }, (_, row) => (
        <div key={row} style={{ display: 'flex' }}>
          {Array.from({ length: size }, (_, col) => {
            const index = cellIndex(row, col, size);
            return (
              <Cell
                key={index}
                index={index}
                row={row}
                col={col}
                digit={doc.solution[index] ?? 0}
                given={doc.givens[index] ?? false}
                editavel={editavel}
                size={size}
                boxW={boxW}
                boxH={boxH}
                px={px}
                onToggle={onToggle}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};
